import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Link } from 'react-router-dom';
import { MODULE_CATALOG, type CatalogImpact } from '@vce/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  FIELD_LABEL,
  QueryState,
  ScoreBadge,
  SEVERITY_LABEL,
  Spinner,
  Stat,
} from '@/components/ui/primitives';
import { useAuth } from '@/app/auth-context';
import { PageHeader } from '@/app/layout';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { api } from '@/lib/api';
import { NO_RUNS_MODULE_HINT, useRunsModuleCta } from '@/lib/module-cta';
import { SCORE_BUCKET_COLORS, scoreTone } from '@/lib/score-visual';
import {
  cn,
  formatCurrencyUsd,
  formatNumber,
  formatPercent,
  formatRelative,
} from '@/lib/utils';

const IMPACT_PERIODS = [
  { days: 7 as const, label: '7 dias' },
  { days: 30 as const, label: '30 dias' },
];

export function DashboardPage() {
  const { tenantId, tenant, isResolving } = useTenantReady();

  const health = useQuery({
    queryKey: ['health', tenantId],
    queryFn: () => api.catalog.health(tenantId as string),
    enabled: tenantId !== null,
  });

  const categories = useQuery({
    queryKey: ['category-health', tenantId],
    queryFn: () => api.catalog.categories(tenantId as string),
    enabled: tenantId !== null,
  });

  const rules = useQuery({
    queryKey: ['rules', tenantId],
    queryFn: () => api.catalog.rules(tenantId as string),
    enabled: tenantId !== null,
  });

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  return (
    <QueryState
      isLoading={health.isLoading}
      isError={health.isError || !health.data}
      loadingLabel="Carregando saúde do catálogo…"
      errorTitle="Não foi possível carregar o dashboard"
      onRetry={() => void health.refetch()}
    >
      {health.data ? (
        <DashboardContent
          tenantId={tenantId}
          data={health.data}
          tenantName={tenant?.accountName}
          hasCredentials={tenant?.hasCredentials ?? true}
          ruleDescriptions={new Map((rules.data ?? []).map((rule) => [rule.id, rule.description]))}
          categories={categories}
        />
      ) : null}
    </QueryState>
  );
}

function DashboardContent({
  tenantId,
  data,
  tenantName,
  hasCredentials,
  ruleDescriptions,
  categories,
}: {
  tenantId: string;
  data: NonNullable<Awaited<ReturnType<typeof api.catalog.health>>>;
  tenantName?: string;
  hasCredentials: boolean;
  ruleDescriptions: Map<string, string>;
  categories: ReturnType<typeof useQuery<Awaited<ReturnType<typeof api.catalog.categories>>>>;
}) {
  const runsCta = useRunsModuleCta();
  const notAudited = data.productCount - data.auditedCount;
  const [impactDays, setImpactDays] = useState<7 | 30>(30);
  const [exportingImpact, setExportingImpact] = useState(false);

  const impact = useQuery({
    queryKey: ['impact', tenantId, impactDays],
    queryFn: () => api.catalog.impact(tenantId, { days: impactDays }),
  });

  async function handleImpactExport(format: 'json' | 'csv') {
    setExportingImpact(true);
    try {
      await api.catalog.exportImpact(tenantId, { days: impactDays, format });
    } finally {
      setExportingImpact(false);
    }
  }

  return (
    <>
      <PageHeader
        title={MODULE_CATALOG.dashboard.label}
        description={
          data.productCount === 0
            ? 'Nenhum produto ingerido ainda.'
            : `${formatNumber(data.productCount)} produtos · última ingestão ${formatRelative(data.lastIngestionAt)} · última auditoria ${formatRelative(data.lastAuditAt)}`
        }
      />

      <div className="space-y-4 p-4 sm:p-6">
        {data.productCount === 0 ? (
          <EmptyState
            title="Catálogo ainda não ingerido"
            description={runsCta.description(
              `Rode uma ingestão em ${MODULE_CATALOG.runs.label} para trazer o catálogo de ${tenantName ?? 'sua conta'}. Nada é escrito na VTEX nessa etapa.`,
            )}
            action={runsCta.action()}
          />
        ) : (
          <>
            {data.auditedCount === 0 ? (
              <Card className="flex flex-col gap-3 border-[var(--color-primary)]/30 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[var(--color-ink)]">
                    Produtos ingeridos, ainda sem auditoria
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--color-muted)]">
                    {runsCta.hasRuns
                      ? 'Rode a auditoria para calcular scores e findings.'
                      : NO_RUNS_MODULE_HINT}
                  </p>
                </div>
                {runsCta.action('Rodar auditoria', 'w-full sm:w-auto')}
              </Card>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Score médio"
                value={data.averageScore.toFixed(1)}
                hint="0 a 100, ponderado por severidade"
                valueTone={scoreTone(data.averageScore)}
              />
              <Stat
                label="Auditados"
                value={formatNumber(data.auditedCount)}
                hint={
                  notAudited > 0 ? `${formatNumber(notAudited)} sem auditoria` : 'catálogo todo'
                }
              />
              <Stat
                label="Problemas"
                value={formatNumber(
                  data.findingsBySeverity.reduce((total, row) => total + row.count, 0),
                )}
                hint={
                  data.findingsBySeverity
                    .filter((row) => row.severity === 'critical')
                    .map((row) => `${formatNumber(row.count)} críticos`)
                    .join('') || 'nenhum crítico'
                }
              />
              <Stat
                label="Na fila"
                value={formatNumber(data.pendingProductCount)}
                hint={
                  data.pendingProductCount > 0
                    ? `${data.pendingSuggestions === 1 ? '1 sugestão' : `${formatNumber(data.pendingSuggestions)} sugestões`} aguardando`
                    : 'fila vazia'
                }
              />
            </div>

            <AgoraStrip
              pendingProductCount={data.pendingProductCount}
              pendingSuggestions={data.pendingSuggestions}
              lastIngestionAt={data.lastIngestionAt}
              lastAuditAt={data.lastAuditAt}
              hasCredentials={hasCredentials}
              notAudited={notAudited}
              runsCta={runsCta}
            />

            <ImpactSection
              days={impactDays}
              onDaysChange={setImpactDays}
              impact={impact}
              onExport={(format) => void handleImpactExport(format)}
              exporting={exportingImpact}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader
                  title="Distribuição de score"
                  description="Quantos produtos caem em cada faixa de qualidade."
                />
                <div className="h-64 px-2 py-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.scoreDistribution}>
                      <XAxis
                        dataKey="bucket"
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        stroke="var(--color-muted)"
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        fontSize={11}
                        width={40}
                        stroke="var(--color-muted)"
                      />
                      <Tooltip
                        cursor={{ fill: 'var(--color-surface-elevated)' }}
                        contentStyle={{
                          background: 'var(--color-surface-card)',
                          border: '1px solid var(--color-hairline)',
                          borderRadius: 10,
                          color: 'var(--color-ink)',
                        }}
                        formatter={(value) => [formatNumber(Number(value)), 'produtos']}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {data.scoreDistribution.map((entry) => (
                          <Cell
                            key={entry.bucket}
                            fill={
                              SCORE_BUCKET_COLORS[
                                entry.bucket as keyof typeof SCORE_BUCKET_COLORS
                              ]
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card>
                <CardHeader
                  title="Cobertura por campo"
                  description="Percentual de produtos com o campo preenchido."
                />
                <div className="divide-y divide-[var(--color-hairline)]">
                  {data.fieldCoverage.map((row) => (
                    <div key={row.field} className="flex items-center gap-3 px-5 py-2.5">
                      <span className="w-28 shrink-0 text-xs font-medium text-[var(--color-ink)] sm:w-40">
                        {FIELD_LABEL[row.field] ?? row.field}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-elevated)]">
                        <div
                          className="h-full rounded-full bg-[var(--color-primary)]"
                          style={{ width: `${row.coverage}%` }}
                        />
                      </div>
                      <span className="w-24 shrink-0 text-right text-xs tabular-nums text-[var(--color-muted)] sm:w-28">
                        {formatPercent(row.coverage)} · {formatNumber(row.missing)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <Card>
              <CardHeader
                title="Problemas por regra"
                description="Ordenado por volume. Clique para ver os produtos afetados."
              />
              {data.findingsByRule.length === 0 ? (
                <EmptyState title="Nenhum problema detectado" />
              ) : (
                <>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--color-surface-soft)] text-left text-xs text-[var(--color-muted)]">
                        <tr>
                          <th className="px-5 py-2 font-medium">Regra</th>
                          <th className="px-5 py-2 font-medium">Severidade</th>
                          <th className="px-5 py-2 text-right font-medium">Ocorrências</th>
                          <th className="px-5 py-2 text-right font-medium">Produtos</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-hairline)]">
                        {data.findingsByRule.map((row) => (
                          <tr
                            key={`${row.ruleId}-${row.severity}`}
                            className="hover:bg-[var(--color-surface-elevated)]"
                          >
                            <td className="px-5 py-2.5">
                              <Link
                                to={`/products?ruleId=${encodeURIComponent(row.ruleId)}`}
                                className="font-medium text-[var(--color-primary)] hover:underline"
                              >
                                {row.ruleId}
                              </Link>
                              <p className="text-xs text-[var(--color-muted)]">
                                {ruleDescriptions.get(row.ruleId) ?? ''}
                              </p>
                            </td>
                            <td className="px-5 py-2.5">
                              <Badge tone={row.severity as 'critical'}>
                                {SEVERITY_LABEL[row.severity] ?? row.severity}
                              </Badge>
                            </td>
                            <td className="px-5 py-2.5 text-right tabular-nums text-[var(--color-ink)]">
                              {formatNumber(row.count)}
                            </td>
                            <td className="px-5 py-2.5 text-right tabular-nums text-[var(--color-ink)]">
                              {formatNumber(row.productCount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="divide-y divide-[var(--color-hairline)] md:hidden">
                    {data.findingsByRule.map((row) => (
                      <Link
                        key={`${row.ruleId}-${row.severity}`}
                        to={`/products?ruleId=${encodeURIComponent(row.ruleId)}`}
                        className="block px-5 py-3 hover:bg-[var(--color-surface-elevated)]"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-[var(--color-primary)]">{row.ruleId}</p>
                          <Badge tone={row.severity as 'critical'}>
                            {SEVERITY_LABEL[row.severity] ?? row.severity}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          {formatNumber(row.count)} ocorrências · {formatNumber(row.productCount)}{' '}
                          produtos
                        </p>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </Card>

            <Card>
              <CardHeader
                title="Score por categoria"
                description="As piores categorias primeiro — é onde o trabalho rende mais."
              />
              {categories.isLoading ? (
                <Spinner />
              ) : categories.isError ? (
                <EmptyState
                  title="Não foi possível carregar categorias"
                  action={
                    <Button variant="secondary" size="sm" onClick={() => void categories.refetch()}>
                      Tentar de novo
                    </Button>
                  }
                />
              ) : (categories.data ?? []).length === 0 ? (
                <EmptyState title="Sem categorias auditadas" />
              ) : (
                <>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full table-fixed text-sm">
                      <colgroup>
                        <col className="w-[26%]" />
                        <col className="w-[12%]" />
                        <col className="w-[14%]" />
                        <col className="w-[12%]" />
                        <col className="w-[36%]" />
                      </colgroup>
                      <thead className="bg-[var(--color-surface-soft)] text-left text-xs text-[var(--color-muted)]">
                        <tr>
                          <th className="px-5 py-2 font-medium">Categoria</th>
                          <th className="px-5 py-2 text-right font-medium">Produtos</th>
                          <th className="px-5 py-2 text-right font-medium">Score médio</th>
                          <th className="px-5 py-2 text-right font-medium">Críticos</th>
                          <th className="px-5 py-2 font-medium">Regras mais frequentes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--color-hairline)]">
                        {(categories.data ?? []).map((row) => (
                          <tr
                            key={String(row.categoryId)}
                            className="hover:bg-[var(--color-surface-elevated)]"
                          >
                            <td className="px-5 py-2.5 text-[var(--color-ink)]">
                              {row.categoryId === null ? (
                                <span className="block truncate">{row.categoryName}</span>
                              ) : (
                                <Link
                                  to={`/products?categoryId=${row.categoryId}`}
                                  className="block truncate text-[var(--color-primary)] hover:underline"
                                >
                                  {row.categoryName}
                                </Link>
                              )}
                            </td>
                            <td className="px-5 py-2.5 text-right tabular-nums">
                              {formatNumber(row.productCount)}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              <ScoreBadge score={row.averageScore} />
                            </td>
                            <td className="px-5 py-2.5 text-right tabular-nums">
                              {formatNumber(row.criticalCount)}
                            </td>
                            <td className="px-5 py-2.5">
                              {row.topRules.length === 0 ? (
                                <span className="text-[var(--color-muted)]">—</span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {row.topRules.slice(0, 3).map((rule) => (
                                    <Badge key={rule.ruleId} tone="neutral">
                                      {rule.ruleId} ({rule.count})
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="divide-y divide-[var(--color-hairline)] md:hidden">
                    {(categories.data ?? []).map((row) => (
                      <div key={String(row.categoryId)} className="px-5 py-3">
                        {row.categoryId === null ? (
                          <p className="font-medium text-[var(--color-ink)]">{row.categoryName}</p>
                        ) : (
                          <Link
                            to={`/products?categoryId=${row.categoryId}`}
                            className="font-medium text-[var(--color-primary)]"
                          >
                            {row.categoryName}
                          </Link>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                          <span>{formatNumber(row.productCount)} produtos</span>
                          <ScoreBadge score={row.averageScore} />
                          <span>{formatNumber(row.criticalCount)} críticos</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}

function AgoraStrip({
  pendingProductCount,
  pendingSuggestions,
  lastIngestionAt,
  lastAuditAt,
  hasCredentials,
  notAudited,
  runsCta,
}: {
  pendingProductCount: number;
  pendingSuggestions: number;
  lastIngestionAt: string | null;
  lastAuditAt: string | null;
  hasCredentials: boolean;
  notAudited: number;
  runsCta: ReturnType<typeof useRunsModuleCta>;
}) {
  const { user } = useAuth();
  const credentialsHref = user?.role === 'owner' ? '/admin' : MODULE_CATALOG.settings.path;
  const activityLabel =
    lastAuditAt || lastIngestionAt
      ? `Ingestão ${formatRelative(lastIngestionAt)} · auditoria ${formatRelative(lastAuditAt)}`
      : 'Sem ingestão ou auditoria ainda';

  const nextStep =
    runsCta.hasRuns && notAudited > 0
      ? { label: 'Rodar auditoria', hint: `${formatNumber(notAudited)} sem auditoria` }
      : runsCta.hasRuns && pendingProductCount === 0
        ? { label: `Ir para ${MODULE_CATALOG.runs.label}`, hint: 'Gerar ou publicar' }
        : null;

  return (
    <Card className="px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-[1.5px] text-[var(--color-muted)]">
        Agora
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <Link
          to={MODULE_CATALOG.review.path}
          className="font-medium text-[var(--color-primary)] hover:underline"
        >
          {pendingProductCount === 0
            ? 'Fila vazia'
            : `${formatNumber(pendingProductCount)} na fila${
                pendingSuggestions > 0
                  ? ` · ${formatNumber(pendingSuggestions)} sugestões`
                  : ''
              }`}
        </Link>
        <span className="hidden text-[var(--color-hairline)] sm:inline" aria-hidden>
          ·
        </span>
        {runsCta.hasRuns ? (
          <Link
            to={MODULE_CATALOG.runs.path}
            className="text-[var(--color-body)] hover:text-[var(--color-ink)] hover:underline"
          >
            {activityLabel}
          </Link>
        ) : (
          <span className="text-[var(--color-body)]">{activityLabel}</span>
        )}
        {!hasCredentials ? (
          <>
            <span className="hidden text-[var(--color-hairline)] sm:inline" aria-hidden>
              ·
            </span>
            <Link
              to={credentialsHref}
              className="font-medium text-[var(--color-severity-critical)] hover:underline"
            >
              Sem credencial VTEX
            </Link>
          </>
        ) : null}
        {nextStep ? (
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-xs text-[var(--color-muted)] sm:inline">
              {nextStep.hint}
            </span>
            {runsCta.action(nextStep.label)}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function ImpactSection({
  days,
  onDaysChange,
  impact,
  onExport,
  exporting,
}: {
  days: 7 | 30;
  onDaysChange: (days: 7 | 30) => void;
  impact: ReturnType<typeof useQuery<CatalogImpact>>;
  onExport: (format: 'json' | 'csv') => void;
  exporting: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="Impacto"
        description="Antes × depois: lift de score, findings críticos e publicações no período."
        action={
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Período">
            {IMPACT_PERIODS.map((period) => {
              const active = period.days === days;
              return (
                <button
                  key={period.days}
                  type="button"
                  onClick={() => onDaysChange(period.days)}
                  className={cn(
                    'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition',
                    active
                      ? 'border-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_14%,transparent)] text-[var(--color-ink)]'
                      : 'border-[var(--color-hairline)] bg-[var(--color-surface-card)] text-[var(--color-muted)] hover:text-[var(--color-ink)]',
                  )}
                >
                  {period.label}
                </button>
              );
            })}
          </div>
        }
      />

      {impact.isLoading ? (
        <Spinner label="Carregando impacto…" />
      ) : impact.isError || !impact.data ? (
        <EmptyState
          title="Não foi possível carregar o impacto"
          action={
            <Button variant="secondary" size="sm" onClick={() => void impact.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <ImpactBody data={impact.data} onExport={onExport} exporting={exporting} />
      )}
    </Card>
  );
}

function ImpactBody({
  data,
  onExport,
  exporting,
}: {
  data: CatalogImpact;
  onExport: (format: 'json' | 'csv') => void;
  exporting: boolean;
}) {
  const writeTotal = data.writeOpsSucceeded + data.writeOpsFailedOrConflict;
  const periodLabel = data.days === 7 ? 'nos últimos 7 dias' : 'nos últimos 30 dias';
  const hasActivity =
    data.suggestionsApplied > 0 ||
    data.fieldsPublished > 0 ||
    data.reviewedCount > 0 ||
    data.writeOpsSucceeded > 0 ||
    data.scoreDeltasMeasured > 0;

  const liftPhrase =
    data.averageScoreLift == null
      ? null
      : data.averageScoreLift === 0
        ? 'score estável após publicação'
        : data.averageScoreLift > 0
          ? `lift médio de +${formatNumber(data.averageScoreLift)} pts no score`
          : `lift médio de ${formatNumber(data.averageScoreLift)} pts no score`;

  const narrative = !hasActivity
    ? `Ainda sem impacto ${periodLabel}.`
    : data.fieldsPublished > 0 || data.uniqueProductsEnriched > 0 || data.scoreDeltasMeasured > 0
      ? `${periodLabel.charAt(0).toUpperCase()}${periodLabel.slice(1)} você publicou ${formatNumber(data.fieldsPublished)} campos em ${formatNumber(data.uniqueProductsEnriched)} produtos${
          liftPhrase ? ` · ${liftPhrase}` : ''
        }${
          data.criticalFindingsCleared > 0
            ? ` · ${formatNumber(data.criticalFindingsCleared)} produtos sem findings críticos`
            : ''
        }.`
      : `${periodLabel.charAt(0).toUpperCase()}${periodLabel.slice(1)} a equipe revisou ${formatNumber(data.reviewedCount)} sugestões (${formatNumber(data.approvedDecisions)} aprovadas · ${formatNumber(data.rejectedDecisions)} recusadas). Publique as aprovadas para fechar o ciclo.`;

  return (
    <div className="space-y-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-sm leading-relaxed text-[var(--color-ink)]">{narrative}</p>
        <div className="flex shrink-0 flex-wrap gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={() => onExport('json')}
          >
            Exportar JSON
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={() => onExport('csv')}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      {hasActivity ? (
        <p className="text-xs text-[var(--color-muted)]">
          {formatNumber(data.reviewedCount)} revisões · ~{formatNumber(data.estimatedReviewMinutes)}{' '}
          min equivalentes de trabalho manual
          {data.reviewedCount > 0
            ? ` · taxa de aprovação ${formatPercent(data.approvalRate)}`
            : ''}
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Lift médio de score"
          value={
            data.averageScoreLift == null
              ? '—'
              : data.averageScoreLift > 0
                ? `+${formatNumber(data.averageScoreLift)}`
                : formatNumber(data.averageScoreLift)
          }
          hint={
            data.scoreDeltasMeasured === 0
              ? 'publique e reaudite para medir'
              : `${formatNumber(data.scoreDeltasMeasured)} produtos medidos`
          }
        />
        <Stat
          label="Lift positivo"
          value={formatNumber(data.productsWithPositiveLift)}
          hint={`${formatNumber(data.productsWithNeutralLift)} estáveis · ${formatNumber(data.productsWithNegativeLift)} negativos`}
        />
        <Stat
          label="Críticos zerados"
          value={formatNumber(data.criticalFindingsCleared)}
          hint="produtos com lift ↑ e sem findings críticos"
        />
        <Stat
          label="Publicações ok"
          value={formatNumber(data.writeOpsSucceeded)}
          hint={
            writeTotal === 0
              ? 'nenhuma escrita no período'
              : `${formatNumber(data.writeOpsFailedOrConflict)} falhas/conflitos · ${formatCurrencyUsd(data.llmCostUsd)} LLM`
          }
        />
      </div>

      {data.topPublishedFields.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
            Campos mais publicados
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.topPublishedFields.map((row) => (
              <Badge key={row.field} tone="neutral">
                {row.field} ({formatNumber(row.count)})
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
